import React from "react";
import { Link } from "react-router-dom";
import { Clock, MapPin, ChevronRight } from "lucide-react";
import { formatNZD } from "@/lib/format";
import { buildAdPath } from "@/lib/slugs";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";

const FALLBACK = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=400&q=60";

/**
 * Homepage "Recently viewed" rail — client-only, localStorage-backed.
 * Hidden when the user has no history or the fetch fails.
 * Spec: Board rev 11 (founder decision #4 · recommended).
 */
export default function RecentlyViewedRail() {
  const { listings, isLoading, isError } = useRecentlyViewed();
  if (isLoading || isError || listings.length === 0) return null;

  return (
    <section
      className="max-w-7xl mx-auto px-4 md:px-8 py-4"
      data-testid="home-recently-viewed"
    >
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest">
            <Clock className="w-3 h-3" /> For you
          </div>
          <h2 className="mt-2 font-display text-xl md:text-2xl font-black text-slate-900">
            Pick up where you left off
          </h2>
        </div>
      </div>

      <div className="grid grid-flow-col auto-cols-[minmax(200px,220px)] md:auto-cols-[minmax(220px,240px)] gap-4 overflow-x-auto pb-2 -mx-1 px-1 scroll-smooth snap-x snap-mandatory">
        {listings.map((l) => (
          <Link
            key={l.id}
            to={buildAdPath(l)}
            data-testid={`recently-viewed-card-${l.id}`}
            className="group block bg-white rounded-2xl shadow-card hover:shadow-card-hover overflow-hidden snap-start card-lift"
          >
            <div className="relative aspect-[4/3] bg-slate-100">
              <img
                src={l.thumb || l.image || l.images?.[0] || FALLBACK}
                alt={l.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            </div>
            <div className="p-3">
              <div className="text-[15px] font-black text-slate-900 tracking-tight">
                {l.price_type === "contact" ? "Contact for price" : formatNZD(l.price)}
              </div>
              <h3 className="mt-1 font-display font-semibold text-sm text-slate-800 leading-snug clamp-2 min-h-[2.4rem] group-hover:text-[color:var(--brand-blue)] transition-colors">
                {l.title}
              </h3>
              <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
                <span className="clamp-1">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  {l.city || l.region || "New Zealand"}
                </span>
                <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
